import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, 
  Briefcase, 
  MapPin, 
  Building2,
  Users,
  Trash2,
  Edit,
  X,
  Upload,
  FileText,
  Eye,
  Search,
  ArrowUpDown
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Jobs() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    company_name: '',
    location: '',
    description: '',
    requirements: [],
    skills: [],
    difficulty_level: 'medium',
    report_email: '',
    agent_name: 'Ema',
    jd_text: ''
  });
  const [newSkill, setNewSkill] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [jdFile, setJdFile] = useState(null);

  const handleJdFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez PDF ou Word.");
      return;
    }
    
    setJdFile(file);
    
    // Extract text from file (send to backend)
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/extract-document`, {
        method: 'POST',
        body: formDataUpload,
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, jd_text: data.text || '' }));
        toast.success("Document importé avec succès");
      } else {
        toast.error("Erreur lors de l'extraction du document");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors de l'upload");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, jobsRes] = await Promise.all([
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
        fetch(`${API}/api/jobs`, { credentials: 'include' })
      ]);

      if (userRes.ok) {
        setUser(await userRes.json());
      }
      if (jobsRes.ok) {
        setJobs(await jobsRes.json());
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingJob 
        ? `${API}/api/jobs/${editingJob.job_id}`
        : `${API}/api/jobs`;
      
      const response = await fetch(url, {
        method: editingJob ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingJob ? "Poste mis à jour" : "Poste créé");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Supprimer ce poste ?")) return;
    
    try {
      const response = await fetch(`${API}/api/jobs/${jobId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Poste supprimé");
        fetchData();
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      department: '',
      company_name: '',
      location: '',
      description: '',
      requirements: [],
      skills: [],
      difficulty_level: 'medium',
      report_email: '',
      agent_name: 'Ema',
      jd_text: ''
    });
    setEditingJob(null);
    setNewSkill('');
    setNewRequirement('');
  };

  const openEditDialog = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      department: job.department,
      company_name: job.company_name || '',
      location: job.location,
      description: job.description,
      requirements: job.requirements || [],
      skills: job.skills || [],
      difficulty_level: job.difficulty_level || 'medium',
      report_email: job.report_email || '',
      agent_name: job.agent_name || 'Ema',
      jd_text: job.jd_text || ''
    });
    setDialogOpen(true);
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [searchJobs, setSearchJobs] = useState("");

  const sortedJobs = useMemo(() => {
    let result = [...jobs];
    if (searchJobs) {
      const q = searchJobs.toLowerCase();
      result = result.filter(j => j.title?.toLowerCase().includes(q) || j.department?.toLowerCase().includes(q) || j.location?.toLowerCase().includes(q));
    }
    return result.sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [jobs, searchJobs, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-ink dark:text-zinc-100">{t('jobs.title')}</h1>
            <p className="text-ink-muted mt-1">{t('jobs.subtitle')}</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-job-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('jobs.createJob')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editingJob ? t('jobs.editJob') : t('jobs.createJob')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t('jobs.form.title')} *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={t('jobs.form.titlePlaceholder')}
                      required
                      data-testid="job-title-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="department">{t('jobs.form.department')} *</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                      placeholder={t('jobs.form.departmentPlaceholder')}
                      required
                      data-testid="job-department-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">{t('jobs.form.companyName')} *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder={t('jobs.form.companyNamePlaceholder')}
                      required
                      data-testid="job-company-name-input"
                    />
                    <p className="text-xs text-ink-muted">{t('jobs.form.companyNameDesc')}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="location">{t('jobs.form.location')} *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder={t('jobs.form.locationPlaceholder')}
                      required
                      data-testid="job-location-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="difficulty_level">{t('jobs.form.difficultyLevel')} *</Label>
                  <div className="flex gap-4">
                    {['easy', 'medium', 'hard'].map((level) => (
                      <label key={level} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="difficulty_level"
                          value={level}
                          checked={formData.difficulty_level === level}
                          onChange={(e) => setFormData(prev => ({ ...prev, difficulty_level: e.target.value }))}
                          className="w-4 h-4 text-lime-600"
                        />
                        <span className={`text-sm font-medium ${
                          level === 'easy' ? 'text-green-600' : 
                          level === 'medium' ? 'text-yellow-600' : 
                          'text-red-600'
                        }`}>
                          {t(`jobs.form.difficulty.${level}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-ink-muted">{t('jobs.form.difficultyDesc')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t('jobs.form.description')} *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('jobs.form.descriptionPlaceholder')}
                    rows={5}
                    required
                    data-testid="job-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jd_text">{t('jobs.form.jobDescription')}</Label>
                  <div className="flex gap-2 mb-2">
                    <label className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleJdFileUpload}
                        className="hidden"
                      />
                      <Upload className="w-4 h-4 mr-2 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {jdFile ? jdFile.name : t('jobs.form.importPdfWord')}
                      </span>
                    </label>
                  </div>
                  <Textarea
                    id="jd_text"
                    value={formData.jd_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, jd_text: e.target.value }))}
                    placeholder={t('jobs.form.jobDescPlaceholder')}
                    rows={4}
                    data-testid="job-jd-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agent_name">{t('jobs.form.agentName')}</Label>
                  <Input
                    id="agent_name"
                    value="Ema"
                    disabled
                    className="bg-slate-100"
                    data-testid="job-agent-name-input"
                  />
                  <p className="text-xs text-ink-muted">{t('jobs.form.agentNameDesc')}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t('jobs.form.skills')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder={t('jobs.form.skillPlaceholder')}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      data-testid="job-skill-input"
                    />
                    <Button type="button" variant="outline" onClick={addSkill}>
                      {t('common.add')}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-1">
                        {skill}
                        <button type="button" onClick={() => removeSkill(index)} className="ml-2">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('jobs.form.requirements')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      placeholder={t('jobs.form.requirementPlaceholder')}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                      data-testid="job-requirement-input"
                    />
                    <Button type="button" variant="outline" onClick={addRequirement}>
                      {t('common.add')}
                    </Button>
                  </div>
                  <div className="space-y-1 mt-2">
                    {formData.requirements.map((req, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-ink-muted">
                        <span>•</span>
                        <span className="flex-1">{req}</span>
                        <button type="button" onClick={() => removeRequirement(index)}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="job-submit-btn">
                    {editingJob ? t('common.save') : t('jobs.createJob')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input value={searchJobs} onChange={e => setSearchJobs(e.target.value)} placeholder={t('common.search') + "..."} className="pl-10" data-testid="jobs-search" />
          </div>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 skeleton rounded-sm" />
            ))}
          </div>
        ) : sortedJobs.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="py-16 text-center">
              <Briefcase className="w-12 h-12 text-ink-muted mx-auto mb-4" />
              <h3 className="font-display font-semibold text-xl text-ink mb-2">
                {t('jobs.noJobs')}
              </h3>
              <p className="text-ink-muted mb-6">
                {t('jobs.noJobsDesc')}
              </p>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('jobs.createJob')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("title")}>
                    <span className="flex items-center gap-1">{t('jobs.table.job')}<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort("department")}>
                    <span className="flex items-center gap-1">{t('jobs.table.department')}<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort("location")}>
                    <span className="flex items-center gap-1">{t('jobs.table.location')}<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("candidates_count")}>
                    <span className="flex items-center gap-1">{t('jobs.table.candidates')}<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell cursor-pointer select-none" onClick={() => toggleSort("status")}>
                    <span className="flex items-center gap-1">{t('jobs.table.status')}<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="text-right">{t('jobs.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedJobs.map((job) => (
                  <TableRow key={job.job_id} className="table-row-hover" data-testid={`job-row-${job.job_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-brand-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-ink">{job.title}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {job.skills?.slice(0, 3).map((skill, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-ink-muted hidden md:flex">
                        <Building2 className="w-4 h-4" />
                        {job.department}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-ink-muted hidden md:flex">
                        <MapPin className="w-4 h-4" />
                        {job.location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-ink-muted" />
                        <span className="font-medium">{job.candidates_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={job.status === 'active' ? 'default' : 'secondary'}
                        className={`hidden sm:inline-flex ${job.status === 'active' ? 'bg-functional-success/20 text-functional-success' : ''}`}
                      >
                        {job.status === 'active' ? t('jobs.status.active') : t('jobs.status.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/jobs/${job.job_id}`)}
                          data-testid={`view-job-${job.job_id}`}
                        >
                          <Eye className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">{t('common.view')}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(job)}
                          data-testid={`edit-job-${job.job_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(job.job_id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-job-${job.job_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
